"use client";

import { ArrowUpRight, Loader, MapPin, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactInfo = [{
  label: "CLINIC ADDRESS",
  value: "1321 5th Ave #202, McKeesport, PA 15132",
  icon: MapPin
}, {
  label: "DIRECT LINE",
  value: "(412) 672-9240",
  icon: Phone
}, {
  label: "REFERRAL FAX",
  value: "412-533-5622",
  icon: Phone
}];
const socialLinks = [{
  label: "REFERRALS",
  href: "/ReferringProvidersPage"
}, {
  label: "DIRECTIONS",
  href: "https://www.google.com/maps/dir/?api=1&destination=1321%205th%20Ave%20%23202%2C%20McKeesport%2C%20PA%2015132"
}];
function CornerTicks() {
  return <>
      <span className="absolute top-0 left-0 w-3 h-px bg-primary" aria-hidden="true" />
      <span className="absolute top-0 left-0 h-3 w-px bg-primary" aria-hidden="true" />
      <span className="absolute top-0 right-0 w-3 h-px bg-primary" aria-hidden="true" />
      <span className="absolute top-0 right-0 h-3 w-px bg-primary" aria-hidden="true" />
      <span className="absolute bottom-0 left-0 w-3 h-px bg-primary" aria-hidden="true" />
      <span className="absolute bottom-0 left-0 h-3 w-px bg-primary" aria-hidden="true" />
      <span className="absolute bottom-0 right-0 w-3 h-px bg-primary" aria-hidden="true" />
      <span className="absolute bottom-0 right-0 h-3 w-px bg-primary" aria-hidden="true" />
    </>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/20 bg-secondary relative rounded-md">
        <CornerTicks />
        <p className="font-mono text-xs text-primary uppercase tracking-widest mb-3">[COMMUNICATION / RECEIVED]</p>
        <p className="text-foreground text-lg mb-6 font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-wider rounded-md border-primary text-primary hover:bg-primary hover:text-white">
          SEND ANOTHER MESSAGE
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-slate-text">
              <span className="text-primary mr-2">01</span> FULL NAME
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your full name" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground placeholder:text-slate-text/40 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-slate-text">
              <span className="text-primary mr-2">02</span> EMAIL ADDRESS
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="email@example.com" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground placeholder:text-slate-text/40 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-slate-text">
              <span className="text-primary mr-2">03</span> CONTACT NUMBER
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(412) 000-0000" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground placeholder:text-slate-text/40 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a reason"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-slate-text">
              <span className="text-primary mr-2">04</span> REASON FOR INQUIRY
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground font-mono text-sm uppercase focus-visible:ring-0 focus-visible:border-primary w-full">
                  <SelectValue placeholder="Select a Reason" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white border-border font-mono text-sm uppercase">
                <SelectItem value="Scheduling Question">Scheduling Question</SelectItem>
                <SelectItem value="Referral Question">Referral Question</SelectItem>
                <SelectItem value="Records Question">Records Question</SelectItem>
                <SelectItem value="Insurance or Referral Requirement Question">Insurance or Referral Requirement Question</SelectItem>
                <SelectItem value="General Office Question">General Office Question</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-slate-text">
              <span className="text-primary mr-2">05</span> MESSAGE
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly tell us what you need help with. Please do not include detailed medical history, symptoms, diagnoses, medications, or urgent concerns in this form." {...field} rows={5} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 text-foreground placeholder:text-slate-text/40 placeholder:font-mono placeholder:text-xs focus-visible:ring-0 focus-visible:border-primary resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-md">
          <p className="font-mono text-xs uppercase tracking-wider text-destructive">[ERROR] {submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-sm uppercase tracking-widest py-6 rounded-md h-auto shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            SENDING...
          </> : <>
            SUBMIT REQUEST <ArrowUpRight className="ml-2 size-4" />
          </>}
      </Button>

      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-text leading-relaxed">
        This form is for general office communication only and should not be used for emergencies or detailed medical concerns. For medical emergencies, call 911.
      </p>
    </Form>;
}
export default function ReachOurOffice() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3767" id="reach-our-office" className="bg-background text-foreground py-32 px-4 sm:px-6 lg:px-12 relative overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="lg:col-span-5 flex flex-col">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-8">[§ Office / INQUIRY]</p>

          <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight mb-8">
            <span className="block text-foreground">CONTACT OUR</span>
            <span className="block text-primary">OFFICE</span>
          </h2>

          <p className="text-slate-text text-base leading-relaxed max-w-[50ch] mb-12 font-default">
            For scheduling questions, referral questions, records questions, or general office inquiries, please call Chaudhry Pulmonary Associates or use the contact form below.
          </p>

          <div className="border-t border-border mb-12">
            {contactInfo.map((info, i) => {
            const Icon = info.icon;
            return <div key={i} data-index={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-5 border-b border-border">
                  <div className="flex items-center gap-2 sm:w-40">
                    <ArrowUpRight className="size-3 text-primary shrink-0" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-text">
                      {info.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <Icon className="size-4 text-slate-text shrink-0 hidden sm:block" />
                    <span className="text-foreground text-sm font-default">{info.value}</span>
                  </div>
                </div>;
          })}
          </div>
          <p className="text-slate-text text-sm mb-12 font-default">
            Referring offices may fax referral information directly to our office.
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            {socialLinks.map((social, i) => <Link key={i} data-index={i} to={social.href} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] uppercase tracking-widest text-slate-text hover:text-primary px-3 py-2 border border-border hover:border-primary transition-colors rounded-md">
                {social.label}
              </Link>)}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative bg-white p-8 sm:p-10 lg:p-12 border border-border shadow-lg rounded-md">
            <CornerTicks />

            <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
              <p className="font-mono text-xs uppercase tracking-widest text-primary">
                CONTACT REQUEST
              </p>
            </div>

            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="inquiry-portal" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service_interest: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            service_interest: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="SUBMIT REQUEST" successMessage="Your message has been received. A member of Dr. Qayyum's staff will contact you shortly." errorMessage="Transmission failed. Please check your connection and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}